<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { relativeMessageStamp } from 'shared/helpers/timeHelper';
import { useLocale } from 'shared/composables/useLocale';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

const { t } = useI18n();
const { resolvedLocale } = useLocale();

const {
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
  isATiktokChannel,
} = useInbox();

const {
  status,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentAttributes,
} = useMessageContext();

const readableTime = computed(() =>
  relativeMessageStamp(createdAt.value, t, resolvedLocale.value)
);

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Don't show status for failed messages, we already show error message
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  // Don't show status for deleted messages
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Messages will be marked as sent for the Email channel if they have a source ID.
  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // API inbox messages use real sent/delivered/read status values from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.SENT;

  // All messages will be mark as sent for the Line channel, as there is no source ID.
  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }
  // API inbox messages use real delivered status from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.DELIVERED;
  // All messages marked as delivered for the web widget inbox once they are sent.
  if (isAWebWidgetInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  return MESSAGE_STATUS.PROGRESS;
});

// SiteUp V2 (F1.7): agora que a linha vive FORA da bolha, o estado do envio
// vira texto legível ("Enviada · 14:05") em vez de depender só do tooltip do
// ícone. Nenhuma lógica nova: é só o `statusToShow` acima virando palavra, e
// só aparece onde já existia indicador de status (saída e template).
const statusLabel = computed(() => {
  if (!showStatusIndicator.value) return '';

  const labelMap = {
    [MESSAGE_STATUS.SENT]: t('CONVERSATION.MESSAGE_STATUS_LABEL.SENT'),
    [MESSAGE_STATUS.DELIVERED]: t(
      'CONVERSATION.MESSAGE_STATUS_LABEL.DELIVERED'
    ),
    [MESSAGE_STATUS.READ]: t('CONVERSATION.MESSAGE_STATUS_LABEL.READ'),
    [MESSAGE_STATUS.PROGRESS]: t('CONVERSATION.MESSAGE_STATUS_LABEL.PROGRESS'),
  };

  return labelMap[statusToShow.value] ?? '';
});
</script>

<template>
  <div class="text-xs flex items-center gap-1">
    <span v-if="isPrivate" class="meta-chip meta-chip--priv">
      <Icon icon="i-ri-lock-line" class="size-2.5" />
      {{ $t('CONVERSATION.ORIGIN.PRIVATE') }}
    </span>
    <template v-if="statusLabel">
      <span>{{ statusLabel }}</span>
      <!-- Separador é decorativo: vem do CSS pra não virar texto traduzível. -->
      <span class="meta-sep" aria-hidden="true" />
    </template>
    <time class="tabular-nums">{{ readableTime }}</time>
    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
  </div>
</template>

<style scoped>
.meta-sep::before {
  content: '·';
}
.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 16px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.meta-chip--wa {
  background: #0d3d2c;
  color: #4de3ab;
}
.meta-chip--api {
  background: #0f3557;
  color: #6cc0ff;
}
.meta-chip--ia {
  background: #2c1f5c;
  color: #b9a4ff;
}
.meta-chip--priv {
  background: #4a3a08;
  color: #ffd95e;
}
/* No claro os fundos escuros não funcionam — inverte pro tom pastel. */
:global(body:not(.dark)) .meta-chip--wa {
  background: #d6f5e8;
  color: #0b6b4a;
}
:global(body:not(.dark)) .meta-chip--api {
  background: #dbeeff;
  color: #0f5896;
}
:global(body:not(.dark)) .meta-chip--ia {
  background: #e7e0ff;
  color: #4a2fb5;
}
:global(body:not(.dark)) .meta-chip--priv {
  background: #fff2c9;
  color: #7a5a00;
}
</style>
